/**
 * useMobileTransfer — Stories 1.6 (Hotspot-Detail, Tab „Data Transfer")
 *
 * Drei schmale Hooks auf die generierten api-types-Endpunkte:
 *  - useHotspotTransferSummary: GET /api/v2/mobile/analytics/summary
 *    gefiltert auf EINEN Hotspot (hotspot_ids=[id]) → GB-Historie + Geräte-$/GB
 *  - useNetworkTransferSummary: derselbe Endpunkt OHNE Filter → Referenzrate
 *    („Netzwerk-Rate" im Sinne des Accounts; es gibt keinen separaten
 *    Netzwerk-Raten-Endpunkt im OpenAPI-Contract)
 *  - useHotspotCarrierBreakdown: GET /api/v2/mobile/analytics/carrier_breakdown
 *    gefiltert auf EINEN Hotspot → Carrier-Verteilung
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { operations } from '@heliumtracker/api-types'

export type TransferPeriod = '24h' | '7d' | '30d' | '90d'

export type TransferSummary =
  operations['getApiV2MobileAnalyticsSummary']['responses'][200]['content']['application/json']['data']

export type CarrierBreakdown =
  operations['getApiV2MobileAnalyticsCarrierBreakdown']['responses'][200]['content']['application/json']['data']

type HookResult<T> = {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useHotspotTransferSummary(
  hotspotId: number | null | undefined,
  period: TransferPeriod = '30d',
): HookResult<TransferSummary> {
  const query = useApi<TransferSummary>(
    ['hotspot', hotspotId, 'transfer-summary', period],
    async () => {
      const result = await apiClient.GET('/api/v2/mobile/analytics/summary', {
        params: { query: { period, hotspot_ids: [hotspotId as number] } },
      })
      if (result.error) return { error: result.error }
      return { data: result.data?.data }
    },
    { enabled: hotspotId != null },
  )
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}

export function useNetworkTransferSummary(
  period: TransferPeriod = '30d',
): HookResult<TransferSummary> {
  const query = useApi<TransferSummary>(
    ['mobile-analytics', 'summary', 'all', period],
    async () => {
      const result = await apiClient.GET('/api/v2/mobile/analytics/summary', {
        params: { query: { period } },
      })
      if (result.error) return { error: result.error }
      return { data: result.data?.data }
    },
  )
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}

export function useHotspotCarrierBreakdown(
  hotspotId: number | null | undefined,
  period: TransferPeriod = '30d',
): HookResult<CarrierBreakdown> {
  const query = useApi<CarrierBreakdown>(
    ['hotspot', hotspotId, 'carrier-breakdown', period],
    async () => {
      const result = await apiClient.GET('/api/v2/mobile/analytics/carrier_breakdown', {
        params: { query: { period, granularity: 'day', hotspot_ids: [hotspotId as number] } },
      })
      if (result.error) return { error: result.error }
      return { data: result.data?.data }
    },
    { enabled: hotspotId != null },
  )
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
